/* Fit checker. Two modes: "product" draws the pieces to scale, "home" lists products that fit. */
(() => {
  if (customElements.get('nt-fit-checker')) return;

  const fill = (tpl, vars) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in vars ? vars[k] : m));
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const joinNames = (names) => names.length < 2 ? (names[0] || '') : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  const TONES = {
    good: ['--nt-positive-bg', '--nt-positive'],
    warn: ['--nt-warning-bg', '--nt-warning'],
    bad: ['--nt-critical-bg', '--nt-critical']
  };

  class NtFitChecker extends HTMLElement {
    connectedCallback() {
      const cfgEl = this.querySelector('[data-fit-config]');
      try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { console.warn('nt-fit-checker: bad config', e); return; }
      this.section = this.closest('.nt-fit');
      this.range = this.querySelector('[data-fit-range]');
      this.q = (sel) => this.querySelectorAll(sel);
      if (this.cfg.mode === 'product') {
        this.prepareProduct();
        if (this.cfg.draw) this.watchVariant();
      }
      let queued = false;
      this.range.addEventListener('input', () => {
        if (queued) return; queued = true;
        requestAnimationFrame(() => { queued = false; this.update(); });
      });
      const sceneEl = this.querySelector('[data-fit-scene]');
      if ('ResizeObserver' in window && sceneEl && sceneEl.parentElement) {
        let lastW = 0;
        new ResizeObserver((en) => { const w = Math.round(en[0].contentRect.width); if (w !== lastW) { lastW = w; this._sceneW = null; this._piecesKey = null; this.update(); } }).observe(sceneEl.parentElement);
      }
      this.mq = window.matchMedia('(min-width: 990px)');
      this.mq.addEventListener?.('change', () => { this._sceneW = null; this._piecesKey = null; this.update(); });
      this.update();
    }

    /* Drawings: pieces from explicit fit pieces, else the product's specifications, else the whole product. */
    drawnPieces() {
      const D = window.NtFitDraw;
      const c = this.cfg;
      const opts = { bottoms: c.bottoms, labels: c.labels, guess: c.guess, hasBasin: c.hasBasin };
      const explicit = (c.pieces || []).filter((p) => !p.whole && p.w > 0 && p.h > 0);
      if (explicit.length) {
        return explicit.map((p) => {
          const n = (p.name || '').toLowerCase();
          const kind = /mirror/.test(n) ? 'mirror' : /tall|towel|column/.test(n) ? 'tall' : /basin|vanity/.test(n) ? 'vanity' : c.guess;
          return { ...p, kind, body: p.h, basin: 0 };
        });
      }
      const size = this.size != null ? this.size : c.size;
      const parts = D.parseSpec(c.spec, size);
      let built = [];
      if (parts.length) {
        if (parts.length === 1 && !parts[0].kind) parts[0].kind = c.guess;
        built = D.buildPieces(parts, opts);
      }
      if (c.layout) built = D.applyLayout(built, c.layout, size, opts);
      if (built.length) return built;
      const whole = (c.pieces || []).find((p) => p.whole && p.w > 0);
      if (whole) {
        const kind = c.guess;
        return D.buildPieces([{ name: '', w: whole.w, h: (c.heights || {})[kind] || whole.h, kind }], opts);
      }
      return [];
    }

    currentOption(re) {
      const form = document.querySelector('.product__info-container') || document;
      const radio = [...form.querySelectorAll('input[type="radio"]:checked')].find((i) => re.test(i.name));
      if (radio) return radio.value;
      const sel = [...form.querySelectorAll('select')].find((x) => re.test(x.name || x.getAttribute('aria-label') || ''));
      return sel ? sel.value : null;
    }

    watchVariant() {
      const refresh = () => {
        const f = this.currentOption(/colou?r|finish/i);
        const z = this.currentOption(/size|width/i);
        const sizeChanged = z != null && z !== this.size;
        if (f != null) this.finish = f;
        if (z != null) this.size = z;
        if (sizeChanged) { this.prepareProduct(); this.update(); } else this.recolor();
      };
      document.addEventListener('change', (e) => { if (e.target.closest && e.target.closest('.product__info-container, variant-selects, variant-radios')) setTimeout(refresh, 30); });
    }

    recolor() {
      if (!this.drawn || !this.pieceEls) return;
      const D = window.NtFitDraw;
      const tones = D.finishTones(this.finish || this.cfg.finish || (this.cfg.layout || {}).finish, D.parseColors(this.cfg.colors));
      this.pieces.forEach((p, i) => { if (this.pieceEls[i]) this.pieceEls[i].innerHTML = D.draw(p, tones); });
    }

    prepareProduct() {
      this.drawn = !!(this.cfg.draw && window.NtFitDraw);
      const source = this.drawn ? this.drawnPieces() : (this.cfg.pieces || []);
      const pieces = source.filter((p) => p.w > 0 && p.h > 0);
      if (this.drawn && pieces.length === 1) pieces[0].whole = true;
      this.pieces = pieces;
      if (!pieces.length) { this.section.hidden = true; return; }
      this.section.hidden = false;
      const gap = this.cfg.gap || 0;
      const row = pieces.filter((p) => !p.above);
      const req = row.filter((p) => !p.optional);
      const span = (list) => list.reduce((s, p) => s + p.w, 0) + gap * Math.max(0, list.length - 1);
      this.needAll = Math.round(span(row));
      this.needMin = Math.round(span(req.length ? req : row));
      const lo = Math.min(...pieces.map((p) => p.bottom));
      const hi = Math.max(...pieces.map((p) => p.bottom + p.h));
      this.floor = this.drawn ? Math.max(0, lo - 20) : Math.max(0, lo - 4);
      this.band = Math.max(20, hi + 1 - this.floor);

      const holder = this.querySelector('[data-fit-pieces]');
      const fb = this.cfg.fallbackImg || {};
      if (this.drawn) {
        const D = window.NtFitDraw;
        const tones = D.finishTones(this.finish || this.cfg.finish || (this.cfg.layout || {}).finish, D.parseColors(this.cfg.colors));
        holder.innerHTML = pieces.map((p, i) => `<div class="nt-fit__draw" data-i="${i}" role="img" aria-label="${esc(p.name)}, ${p.w}cm">${D.draw(p, tones)}</div>`).join('');
      } else {
      pieces.forEach((p) => {
        if (p.img) return;
        const n = (p.name || '').toLowerCase();
        p.img = /mirror|cabinet above|wall cabinet/.test(n) && p.above ? fb.mirror : /tall|tower|column/.test(n) ? fb.tall : /mirror/.test(n) ? fb.mirror : fb.vanity || '';
      });
      holder.innerHTML = pieces.map((p, i) => p.img
        ? `<img data-i="${i}" src="${esc(p.img)}" alt="${esc(p.name)}, ${p.w}cm" loading="lazy">`
        : `<div class="nt-fit__box" data-i="${i}">${esc(p.name)}</div>`).join('');
      }
      this.pieceEls = [...holder.children];
      this._piecesKey = null; this._sceneW = null;

      const legend = this.querySelector('[data-fit-legend]');
      if (legend) {
        const parts = pieces.length === 1 && pieces[0].whole
          ? [`<span>${esc(pieces[0].w)} cm wide</span>`]
          : row.concat(pieces.filter((p) => p.above)).map((p) => `<span>${esc(p.name)} ${p.w}cm</span>`);
        if (this.cfg.legendSuffix) parts.push(`<span>${esc(this.cfg.legendSuffix)}</span>`);
        legend.innerHTML = parts.join('<i>·</i>');
      }
    }

    tone(t) {
      this.dataset.tone = t;
      if (this.dataset.tint === 'true' && this.section) {
        this.section.style.setProperty('--nt-bg', `var(${TONES[t][0]})`);
      }
      this.section?.style.setProperty('--nt-tone-ink', `var(${TONES[t][1]})`);
    }

    update() {
      const wall = parseInt(this.range.value, 10) || 0;
      const m = this.cfg.msg;
      this.q('[data-fit-value]').forEach((el) => (el.textContent = wall + ' cm'));
      this.q('[data-fit-inches]').forEach((el) => (el.textContent = fill(this.cfg.inches, { inches: Math.round(wall / 2.54) })));
      if (this.cfg.mode === 'product') this.updateProduct(wall, m); else this.updateHome(wall, m);
    }

    updateProduct(wall, m) {
      if (!this.pieces || !this.pieces.length) return;
      const gap = this.cfg.gap || 0;
      const optional = this.pieces.filter((p) => p.optional && !p.above);
      const optNames = joinNames(optional.map((p) => p.name.toLowerCase()));
      const allNames = this.pieces.length === 1 && this.pieces[0].whole ? 'it' : 'the ' + joinNames(this.pieces.map((p) => p.name.toLowerCase()));
      let state, title, note, spare;
      const v = { wall, spare: wall - this.needAll, short: this.needAll - wall, min: this.needMin, over: this.needMin - wall, optional: optNames, pieces: allNames };
      const single = this.pieces.length === 1 && this.pieces[0].whole;
      if (wall >= this.needAll) { state = 'good'; title = m.goodTitle; note = single && m.goodNoteSingle ? m.goodNoteSingle : m.goodNote; spare = m.spare; }
      else if (wall >= this.needMin && optional.length) { state = 'warn'; title = m.warnTitle; note = m.warnNote; spare = m.elsewhere; }
      else { state = 'bad'; title = m.badTitle; note = m.badNote; spare = m.over; }
      this.tone(state);
      this.q('[data-fit-title]').forEach((el) => (el.textContent = fill(title, v)));
      this.q('[data-fit-note]').forEach((el) => (el.textContent = fill(note, v)));
      this.q('[data-fit-spare]').forEach((el) => (el.textContent = fill(spare, v)));
      this.q('[data-fit-ruler]').forEach((el) => (el.textContent = fill(m.wall, v)));

      this.layoutScene(wall, state !== 'good');
      if (!this._ready) { this._ready = true; const sc = this.querySelector('[data-fit-scene]'); requestAnimationFrame(() => requestAnimationFrame(() => sc.classList.add('is-ready'))); }
    }

    /* Fixed pixel scale (px per cm), worked out from the panel width and the slider's maximum, so the product keeps
       exactly the same size while the shopper drags: only the wall (and the measurements) move. */
    pxPerCm() {
      const scene = this.querySelector('[data-fit-scene]');
      const box = scene.parentElement;
      const cs = getComputedStyle(box);
      const avail = Math.max(120, box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight));
      /* fill the panel width for the slider's full range, but keep tall sets to a sensible height */
      const span = Math.max(parseInt(this.range.max, 10) || 0, this.needAll || 0, 1);
      const maxH = this.mq.matches ? 440 : 340;
      const maxK = 1.6 * (this.cfg.scale || 1.5);
      return { k: Math.min(avail / span, maxH / (this.band || 1), maxK), avail };
    }

    layoutScene(wall, dropOptional) {
      const scene = this.querySelector('[data-fit-scene]');
      const { k, avail } = this.pxPerCm();
      const px = (cm) => (cm * k).toFixed(2) + 'px';
      const width = Math.max(wall, this.needAll) * k;
      if (this._sceneW !== avail || this._k !== k) {
        this._sceneW = avail; this._k = k;
        scene.style.width = Math.min(avail, Math.max(width, parseInt(this.range.max, 10) * k)).toFixed(1) + 'px';
        scene.style.height = (this.band * k).toFixed(1) + 'px';
        scene.style.aspectRatio = '';
      }
      const gap = this.cfg.gap || 0;
      const key = dropOptional ? 'drop' : 'all';
      if (this._piecesKey !== key + k) {
        this._piecesKey = key + k;
        let cursor = 0, prev = null;
        this.pieces.forEach((p, i) => {
          const el = this.pieceEls[i];
          if (p.above) {
            const hidden = !prev || prev.hidden;
            el.style.display = hidden ? 'none' : '';
            if (!hidden) { el.style.left = px(prev.left + (prev.w - p.w) / 2); el.style.width = px(p.w); }
          } else {
            const hidden = dropOptional && p.optional;
            el.style.display = hidden ? 'none' : '';
            prev = { left: cursor, w: p.w, hidden };
            if (!hidden) { el.style.left = px(cursor); el.style.width = px(p.w); cursor += p.w + gap; }
          }
          el.style.bottom = px(p.bottom - this.floor);
          el.style.height = px(p.h);
        });
      }
      /* the wall follows the slider directly (no easing), on the compositor where possible */
      const wallPx = wall * k;
      const wallEl = this.querySelector('[data-fit-wall]');
      if (wallEl) wallEl.style.width = wallPx.toFixed(1) + 'px';
      this.querySelector('[data-fit-beyond]').style.left = wallPx.toFixed(1) + 'px';
      this.querySelector('[data-fit-floor]').style.width = wallPx.toFixed(1) + 'px';
    }

    updateHome(wall, m) {
      const list = this.querySelector('[data-fit-matches]');
      const products = this.cfg.products || [];
      let fits = 0;
      list.innerHTML = products.map((p) => {
        const left = wall - p.needs;
        const ok = left >= 0;
        if (ok) fits++;
        const verdict = !ok ? ['bad', m.no] : left < 10 ? ['warn', m.just] : ['good', m.fits];
        return `<a class="nt-fit__match" href="${esc(p.url)}" data-fits="${ok}">
          ${p.img ? `<img src="${esc(p.img)}" alt="" loading="lazy" width="54" height="54">` : ''}
          <div style="flex:1;min-width:0"><div class="nt-fit__match-name">${esc(p.title)}</div>
          <div class="nt-fit__match-needs">${esc(fill(m.needs, { needs: p.needs }))}</div></div>
          <span class="nt-verdict nt-verdict--${verdict[0]}">${esc(verdict[1])}</span></a>`;
      }).join('');
      this.tone(fits === products.length ? 'good' : fits > 0 ? 'warn' : 'bad');
      const cta = this.querySelector('[data-fit-cta]');
      if (cta) cta.textContent = fill(m.cta, { count: fits });
    }
  }
  customElements.define('nt-fit-checker', NtFitChecker);
})();
